import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Grid, 
  Typography, 
  Box, 
  Container, 
  Alert,
  CircularProgress,
} from '@mui/material';
import FormQuestions from './components/FormQuestions';
import FormQuestionsPreview from './components/FormQuestionsPreview';
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import Sidebar from './components/Sidebar';
import { postApi } from './api/services';
import { useNavigate } from 'react-router-dom';
import MainLoader from './components/MainLoader';
import i18next from 'i18next';

const SatisfactionQuestionnaire = () => {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasExistingSurvey, setHasExistingSurvey] = useState(false);
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [isCheckingSurvey, setIsCheckingSurvey] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const participantSatisfactionQuestions = [
    {
      title: "1. The amount of new hair growth you have experienced?",
      type: "radio",
      commonIdentifier: "new_hair_growth_satisfaction",
      optionMap: {
        option_1: '1 - Very dissatisfied',
        option_2: '2 - Dissatisfied',
        option_3: '3 - Neutral',
        option_4: '4 - Satisfied',
        option_5: '5 - Very satisfied'
      },
      questionId: 1
    },
    {
      title: "2. The thickness or density of the regrown hair?",
      type: "radio",
      commonIdentifier: "hair_thickness_satisfaction",
      optionMap: {
        option_1: '1 - Very dissatisfied',
        option_2: '2 - Dissatisfied',
        option_3: '3 - Neutral',
        option_4: '4 - Satisfied',
        option_5: '5 - Very satisfied'
      },
      questionId: 2
    },
    {
      title: "3. The natural appearance of the regrown hair?",
      type: "radio",
      commonIdentifier: "hair_appearance_satisfaction",
      optionMap: {
        option_1: '1 - Very dissatisfied',
        option_2: '2 - Dissatisfied',
        option_3: '3 - Neutral',
        option_4: '4 - Satisfied',
        option_5: '5 - Very satisfied'
      },
      questionId: 3
    },
    {
      title: "4. The speed at which you noticed visible results?",
      type: "radio",
      commonIdentifier: "speed_of_results_satisfaction",
      optionMap: {
        option_1: '1 - Very dissatisfied',
        option_2: '2 - Dissatisfied',
        option_3: '3 - Neutral',
        option_4: '4 - Satisfied',
        option_5: '5 - Very satisfied'
      },
      questionId: 4
    },
    {
      title: "5. The ease of use or convenience of the treatment?",
      type: "radio",
      commonIdentifier: "treatment_convenience_satisfaction",
      optionMap: {
        option_1: '1 - Very dissatisfied',
        option_2: '2 - Dissatisfied',
        option_3: '3 - Neutral',
        option_4: '4 - Satisfied',
        option_5: '5 - Very satisfied'
      },
      questionId: 5
    },
    {
      title: "6. Your overall satisfaction with the treatment?",
      type: "radio",
      commonIdentifier: "overall_treatment_satisfaction",
      optionMap: {
        option_1: '1 - Very dissatisfied',
        option_2: '2 - Dissatisfied',
        option_3: '3 - Neutral',
        option_4: '4 - Satisfied',
        option_5: '5 - Very satisfied'
      },
      questionId: 6
    }
  ];

  // Check for existing survey on component mount
  useEffect(() => {
    checkExistingSurvey();
  }, []);

  const checkExistingSurvey = async () => {
    setIsCheckingSurvey(true);
    try {
      const userObj = JSON.parse(localStorage.getItem("user_obj"))
      const params = { user_id: userObj.user_id };
      
      console.log('Checking existing survey with params:', params);
      
      const response = await postApi('list_study_participant_satisfaction_surveys', params);
      
      console.log('Survey check response:', response);
      
      if (response && response.surveys && response.surveys.length > 0) {
        // Survey exists, set up preview mode
        setHasExistingSurvey(true);
        populatePreviewQuestions(response.surveys[0]); // Use the first survey
      } else {
        // No survey exists, show editable form
        setHasExistingSurvey(false);
      }
    } catch (error) {
      console.error('Error checking existing survey:', error);
      // If API fails, assume no survey exists and show editable form
      setHasExistingSurvey(false);
    } finally {
      setIsCheckingSurvey(false);
    }
  };

  const populatePreviewQuestions = (surveyData) => {
    const questionsWithValues = participantSatisfactionQuestions.map(question => {
      const questionKey = question.commonIdentifier;
      const surveyValue = surveyData[questionKey];
      
      if (surveyValue !== undefined) {
        // Find the option text that matches the numeric value
        const optionText = Object.values(question.optionMap).find(option => {
          const optionValue = parseInt(option.split(' - ')[0]);
          return optionValue === surveyValue;
        });
        
        return {
          ...question,
          value: optionText || surveyValue.toString()
        };
      }
      return question;
    });
    
    setPreviewQuestions(questionsWithValues);
    
    // Also populate formValues for when user switches to edit mode
    const initialFormValues = {};
    questionsWithValues.forEach(question => {
      if (question.value) {
        initialFormValues[question.commonIdentifier] = question.value;
      }
    });
    setFormValues(initialFormValues);
  };

  const handleFormValues = (value, identifier) => {
    setFormValues(prev => ({
      ...prev,
      [identifier]: value
    }));
  };

  const preparePayload = () => {
    const userObj = JSON.parse(localStorage.getItem("user_obj"))
    const payload = {
      user_id: userObj.user_id
    };

    Object.keys(formValues).forEach(key => {
      const value = formValues[key];
      const score = parseInt(value.split(' - ')[0]);
      payload[key] = score;
    });

    return payload;
  };

  const handleSubmit = async () => {
    const answeredQuestions = Object.keys(formValues).length;
    if (answeredQuestions < participantSatisfactionQuestions.length) {
      alert(`Please answer all ${participantSatisfactionQuestions.length} questions before submitting. You have answered ${answeredQuestions}.`);
      return;
    }

    setIsLoading(true);
    
    try {
      const payload = preparePayload();
      
      console.log('Submitting Participant satisfaction payload:', payload);
      
      const response = await postApi('submit_study_participant_satisfaction_survey', payload);
      
      console.log('Participant satisfaction submission response:', response);
      setIsSubmitted(true);

      // Redirect immediately after showing success message
      setTimeout(() => {
        navigate('/reports');
      }, 1500);
      
    } catch (error) {
      console.error('Participant satisfaction submission failed:', error);
      alert('Failed to submit questionnaire. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setFormValues({})
  };

  // Show loading while checking for existing survey
  if (isCheckingSurvey) {
    return (
      <>
      <MainLoader />
      <div className="feedbackContainer photo-container main-section-container">
        <Grid container spacing={0} className="d-flex justify-content-start align-items-center header">
          {/* <Grid item xs={1}>
            <div className="d-flex justify-content-start align-items-center">
              <span className="backButtonDiv_feedbackform" onClick={() => navigate(-1)}>
                <ArrowBackOutlinedIcon className="arrowBackBtn_feedbackform" />
              </span>
            </div>
          </Grid> */}
          <Grid item xs={11} className="d-flex justify-content-center align-items-center reportPageHead">
            <Typography variant="h6" className="title ml-0px">
              {i18next.t("Feedback")}
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Sidebar />
          </Grid>
        </Grid>
      </div>
      </>
    );
  }

  return (
    <>
      {isLoading && <MainLoader/>}
      <div className="feedbackContainer photo-container main-section-container">
        <Grid container spacing={0} className="d-flex justify-content-start align-items-center header">
          {/* <Grid item xs={1} className="">
            <div className="d-flex justify-content-start align-items-center">
              <span className="backButtonDiv_feedbackform" onClick={() => navigate('/reports')}>
                <ArrowBackOutlinedIcon className="arrowBackBtn_feedbackform" />
              </span>
            </div>
          </Grid> */}
          <Grid item xs={11} className="d-flex justify-content-center align-items-center reportPageHead">
            <Typography variant="h6" className="title ml-0px">
              {i18next.t("Feedback")}
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Sidebar />
          </Grid>
        </Grid>

        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Typography variant="body1" paragraph sx={{ mb: 3 }} className="instructionText">
            <strong>{i18next.t("Instructions:")}</strong> {i18next.t("Please rate your satisfaction with the following aspects of your hair regrowth treatment over the past month.")}
          </Typography>

          <div className="questions-container">
            {hasExistingSurvey && !isEditing ? (
              // Show preview mode with all options (one selected)
              previewQuestions.map((question) => (
                <Grid item xs={12} key={question.questionId} sx={{ mb: 4 }}>
                  <FormQuestionsPreview 
                    element={question}
                    handleFormValues={handleFormValues}
                    formValues={formValues}
                    formQuestions={previewQuestions}
                    showQuesAns={false} // Changed to false to show all options
                    from={"satisfaction"}
                  />
                </Grid>
              ))
            ) : (
              // Show editable form (either new survey or editing existing)
              participantSatisfactionQuestions.map((question) => (
                <Grid item xs={12} key={question.questionId} sx={{ mb: 4 }}>
                  <FormQuestions 
                    element={{
                      ...question,
                      value: formValues[question.commonIdentifier] // Prefill with existing values
                    }}
                    handleFormValues={handleFormValues}
                    formValues={formValues}
                    formQuestions={participantSatisfactionQuestions}
                    from={"satisfaction"}
                  />
                </Grid>
              ))
            )}
          </div>

          {/* Show edit button and info when in preview mode */}
          {hasExistingSurvey && !isEditing && (
            <Box textAlign="center" mt={4}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body1">
                  You have already submitted this. You can update your responses by clicking the Edit button below.
                </Typography>
              </Alert>
              <Box>
                <Button 
                  variant="contained" 
                  onClick={handleEdit}
                  sx={{ minWidth: 120 }}
                  className='commonButton'
                >
                  Edit Responses
                </Button>
              </Box>
            </Box>
          )}

          {/* Show submit button when editing or new survey */}
          {(isEditing || !hasExistingSurvey) && (
            <Box textAlign="center" mt={4}>
              <Button 
                variant="contained" 
                size="large"
                onClick={handleSubmit}
                className='commonButton'
                disabled={isLoading || isSubmitted || Object.keys(formValues).length < participantSatisfactionQuestions.length}
                sx={{ 
                  minWidth: 200, 
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Submitting...
                  </Box>
                ) : isSubmitted ? (
                  'Submitted!'
                ) : (
                  'Submit'
                )}
              </Button>
              
              {Object.keys(formValues).length < participantSatisfactionQuestions.length && !isSubmitted && (
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Please answer all {participantSatisfactionQuestions.length} questions to submit ({Object.keys(formValues).length}/{participantSatisfactionQuestions.length} answered)
                </Typography>
              )}
            </Box>
          )}
        </Container>
      </div>
    </>
  );
};

export default SatisfactionQuestionnaire;